import { useEffect, useRef, useState } from "react";
import { Play, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { meteredAudio } from "@/lib/voiceMeter";
import { aiError } from "./aiError";

// One AI voice per video, with a play button to hear each.
export function VoicePicker({ voice, setVoice, disabled }) {
  const [voices, setVoices] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const cache = useRef({});
  const audioRef = useRef(null);

  useEffect(() => { api.get("/ai/voices").then((r) => setVoices(r.data.voices)).catch(() => {}); }, []);
  useEffect(() => () => audioRef.current?.pause(), []);

  const stop = () => { audioRef.current?.pause(); setPlayingId(null); };
  const preview = async (id) => {
    if (playingId === id) { stop(); return; }
    stop();
    let url = cache.current[id];
    if (!url) {
      setLoadingId(id);
      try {
        const r = await api.post("/ai/speak", { voice: id, preview: true }, { responseType: "blob" });
        url = cache.current[id] = URL.createObjectURL(r.data);
      } catch (e) {
        await aiError(e);
        setLoadingId(null);
        return;
      }
      setLoadingId(null);
    }
    const a = meteredAudio(url);
    audioRef.current = a;
    a.onended = () => setPlayingId(null);
    setPlayingId(id);
    a.play().catch(() => setPlayingId(null));
  };

  return (
    <div data-testid="voice-picker">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" role="radiogroup">
        {voices.map((v) => {
          const active = v.id === voice;
          return (
            <div key={v.id} data-testid={`voice-option-${v.id}`}
              className={`flex items-center gap-2 border rounded-[4px] pl-3 pr-1.5 py-1.5 transition-colors ${active ? "border-rose-500 bg-rose-50" : "border-zinc-200 hover:border-zinc-300"}`}>
              <button type="button" disabled={disabled} onClick={() => setVoice(v.id)} data-testid={`voice-select-${v.id}`} className="flex-1 text-left min-w-0">
                <span className="text-sm font-medium text-zinc-900">{v.label}</span>
                <span className="text-xs text-zinc-500 ml-2">{v.desc}</span>
              </button>
              <button type="button" onClick={() => preview(v.id)} data-testid={`voice-preview-${v.id}`} title="Hear this voice"
                className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-zinc-600 hover:text-rose-600 hover:bg-white transition-colors">
                {loadingId === v.id ? <Loader2 size={14} className="animate-spin" /> : playingId === v.id ? <Square size={12} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
